import { useCallback, useEffect, useState } from 'react';
import type {
  ChangeEvent,
  ChangeEventHandler,
  Dispatch,
  FocusEvent,
  KeyboardEvent,
  RefObject,
  SetStateAction,
} from 'react';
import type { VariableSizeList } from 'react-window';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

import type { ScrollingStatus } from './types/scrolling-status.type';

interface Props {
  readonly pageListRef: RefObject<VariableSizeList<unknown> | null>;
  readonly scrollingStatus: ScrollingStatus;
  readonly setScale: Dispatch<SetStateAction<number>>;
  readonly setScrollingStatus: Dispatch<SetStateAction<ScrollingStatus>>;
  readonly totalPageCount: number;
}

// MHO : from https://codesandbox.io/s/pdf-viewer-w7wpu
export const Controls = ({
  pageListRef,
  scrollingStatus,
  setScale,
  setScrollingStatus,
  totalPageCount,
}: Readonly<Props>) => {
  const [pageNumberInputValue, setPageNumberInputValue] = useState(
    scrollingStatus.pageNumber.toFixed(),
  );

  const handlePageNumberChange: ChangeEventHandler<HTMLInputElement> =
    useCallback((event: Readonly<ChangeEvent<HTMLInputElement>>) => {
      setPageNumberInputValue(event.target.value);
    }, []);

  const [isInEditionMode, setIsInEditionMode] = useState(false);

  const enterEditionMode = useCallback(() => {
    setPageNumberInputValue(scrollingStatus.pageNumber.toFixed());
    setIsInEditionMode(true);
  }, [scrollingStatus.pageNumber]);

  const leaveEditionMode = useCallback(
    (
      event:
        | Readonly<FocusEvent<HTMLInputElement | HTMLTextAreaElement>>
        | Readonly<KeyboardEvent<HTMLDivElement>>,
    ) => {
      if ('key' in event && event.key !== 'Enter') {
        if (event.key !== 'Escape') {
          return;
        }

        setIsInEditionMode(false);
        return;
      }

      const valueAsNumber = parseInt(pageNumberInputValue, 10);

      if (Number.isNaN(valueAsNumber)) {
        setIsInEditionMode(false);
        return;
      }

      setScrollingStatus({
        isControlled: true,
        pageNumber: Math.max(Math.min(valueAsNumber, totalPageCount), 1),
      });

      setIsInEditionMode(false);
    },
    [pageNumberInputValue, setScrollingStatus, totalPageCount],
  );

  useEffect(() => {
    if (!scrollingStatus.isControlled) {
      return;
    }

    pageListRef.current?.scrollToItem(scrollingStatus.pageNumber - 1, 'start');

    // HACK : we don't want `onItemsRendered` to be executed just after scrollToIem
    // because (i don't known why) `onItemsRendered` gets executed several times after a page jump
    // and value returned by the last execution isn't the good one (it considers that the
    // current displayed page is the previous one)
    // Since `onItemsRendered` won't do anything when `isControlled` is true,
    // Following code will block `onItemsRendered` callback for 500ms
    // Tradeoff : During this time, manual scroll won't change page number
    // I don't like this solution but I don't have any other for now
    setTimeout(() => {
      setScrollingStatus((prev) => ({ ...prev, isControlled: false }));
    }, 500);
  }, [
    pageListRef,
    scrollingStatus.isControlled,
    scrollingStatus.pageNumber,
    setScrollingStatus,
  ]);

  const handleNext = useCallback(() => {
    if (scrollingStatus.pageNumber === totalPageCount) {
      return;
    }

    setScrollingStatus((prev) => ({
      ...prev,
      isControlled: true,
      pageNumber: prev.pageNumber + 1,
    }));
  }, [scrollingStatus.pageNumber, setScrollingStatus, totalPageCount]);

  const handlePrevious = useCallback(() => {
    if (scrollingStatus.pageNumber === 1) {
      return;
    }

    setScrollingStatus((prev) => ({
      ...prev,
      isControlled: true,
      pageNumber: prev.pageNumber - 1,
    }));
  }, [scrollingStatus.pageNumber, setScrollingStatus]);

  const handleZoomIn = useCallback(() => {
    setScale((v) => v + 0.1);
  }, [setScale]);

  const handleZoomOut = useCallback(() => {
    setScale((v) => v - 0.1);
  }, [setScale]);

  return (
    <Box
      id="pdf-controls"
      sx={{
        bottom: 0,
        display: 'flex',
        left: '50%',
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        zIndex: 999,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          alignItems: 'center',
          display: 'flex',
          verticalAlign: 'middle',
          textAlign: 'center',
        }}
        variant="outlined"
      >
        <IconButton
          aria-label="Page précédente"
          disabled={scrollingStatus.pageNumber === 1}
          id="pdf-controls-previous-page"
          onClick={handlePrevious}
          size="small"
        >
          <NavigateBeforeIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="Page suivante"
          disabled={scrollingStatus.pageNumber === totalPageCount}
          id="pdf-controls-next-page"
          onClick={handleNext}
          size="small"
        >
          <NavigateNextIcon fontSize="small" />
        </IconButton>
        {!isInEditionMode ? (
          <Typography
            color="textPrimary"
            component={Button}
            id="pdf-controls-current-page"
            onClick={enterEditionMode}
            variant="body2"
          >
            {scrollingStatus.pageNumber} /{' '}
            <span id="pdf-controls-total-page-count">{totalPageCount}</span>
          </Typography>
        ) : (
          <TextField
            autoFocus
            id="pdf-controls-current-page-edition-mode"
            margin="none"
            onBlur={leaveEditionMode}
            onChange={handlePageNumberChange}
            onKeyDown={leaveEditionMode}
            size="small"
            slotProps={{ htmlInput: { min: 1, max: totalPageCount, step: 1 } }}
            type="number"
            value={pageNumberInputValue}
            variant="outlined"
          />
        )}
        <IconButton
          aria-label="Zoomer"
          id="pdf-controls-zoom-in"
          onClick={handleZoomIn}
          size="small"
        >
          <ZoomInIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="Dézoomer"
          id="pdf-controls-zoom-out"
          onClick={handleZoomOut}
          size="small"
        >
          <ZoomOutIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Box>
  );
};
